import { auth } from './firebase';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:9910';

export class ApiError extends Error {
  constructor(public status: number, public body: string) {
    super(`API error ${status}: ${body}`);
  }
}

export async function apiFetch(
  path: string,
  idToken: string | null,
  init: RequestInit = {},
): Promise<Response> {
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (idToken) {
    // The caller wants an authenticated request. Always pull the
    // current token from Firebase rather than trust the one in
    // component state — Firebase ID tokens expire after 1h and the
    // background refresh isn't 100% reliable (tab backgrounded,
    // browser throttling, network blip). `getIdToken()` returns the
    // cached value when still valid and refreshes when expired, so
    // every request goes out with a usable token. Falls back to the
    // passed value if there's somehow no current user.
    const fresh = (await auth.currentUser?.getIdToken()) ?? idToken;
    headers.set('Authorization', `Bearer ${fresh}`);
  }
  return fetch(`${API_URL}${path}`, { ...init, headers });
}

async function apiGet<T>(path: string, idToken: string): Promise<T> {
  const res = await apiFetch(path, idToken);
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new ApiError(res.status, body);
  }
  return res.json();
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export type WhoAmI = {
  email: string;
  name?: string;
  roles: string[];
};

export function whoami(idToken: string): Promise<WhoAmI> {
  return apiGet<WhoAmI>('/api/v1/auth/whoami', idToken);
}

// ---------------------------------------------------------------------------
// Orders
// ---------------------------------------------------------------------------

// Money values come from the backend as strings (rust_decimal Display) to
// preserve precision. UI parses/formats as needed.
export type Order = {
  id: string;
  customer_id: string | null;
  guest_email: string | null;
  customer_email: string;
  legacy_mongo_id: string | null;
  legacy_business_id: string | null;
  is_test: boolean;
  status: string;
  subtotal: string;
  tax_total: string;
  shipping_total: string;
  discount_total: string;
  total: string;
  payment_method: string | null;
  payment_charge_id: string | null;
  delivery_address: unknown | null;
  customer_address: unknown | null;
  shipping_method: string | null;
  created_at: string;
  updated_at: string;
  paid_at: string | null;
  shipped_at: string | null;
  fulfilled_at: string | null;
  cancelled_at: string | null;
  refunded_at: string | null;
};

export type LineItem = {
  id: number;
  order_id: string;
  line_position: number;
  tenant: string;
  item_id: string;
  variant_id: string | null;
  qty: number;
  unit_price: string;
  line_total: string;
  snapshot_name: string;
  snapshot_brand: string | null;
  snapshot_image_url: string | null;
  snapshot_tax_rate: string | null;
};

export type OrderWithLines = Order & { lines: LineItem[] };

export type OrdersPage = {
  items: Order[];
  total: number;
  limit: number;
  offset: number;
};

export function listOrders(
  idToken: string,
  email: string | null,
  includeTest: boolean,
  includeCancelled: boolean,
  since: string | null,
  limit: number,
  offset: number,
): Promise<OrdersPage> {
  const params = new URLSearchParams();
  if (email) params.set('email', email);
  if (includeTest) params.set('include_test', 'true');
  if (includeCancelled) params.set('include_cancelled', 'true');
  if (since) params.set('since', since);
  params.set('limit', String(limit));
  params.set('offset', String(offset));
  return apiGet<OrdersPage>(`/api/v1/orders?${params.toString()}`, idToken);
}

export function getOrder(idToken: string, id: string): Promise<OrderWithLines> {
  return apiGet<OrderWithLines>(`/api/v1/orders/${encodeURIComponent(id)}`, idToken);
}

/** One card authorisation as Paysafe holds it, successful or not.
 *  Everything past `id` is optional: a declined auth carries `error`
 *  and no `auth_code`, a successful one the reverse. */
export type PaymentAttempt = {
  /** "LIVE" or "TEST" — a sandbox auth moved no money. */
  environment: string;
  id: string;
  merchantRefNum?: string | null;
  /** Minor units (cents). */
  amount?: number | null;
  /** "COMPLETED" | "FAILED" | "PENDING" | ... */
  status?: string | null;
  txnTime?: string | null;
  authCode?: string | null;
  error?: { code?: string | null; message?: string | null } | null;
  card?: { lastDigits?: string | null; type?: string | null } | null;
  avsResponse?: string | null;
  cvvVerification?: string | null;
};

export type PaymentAttempts = {
  /** Environments actually queried — one with no credentials is skipped. */
  checked: string[];
  /** The window searched. Paysafe searches a date range, not all
   *  history, so an empty result only means something against this. */
  searched_from: string;
  searched_to: string;
  /** The order predates what Paysafe will search (a year). The lookup
   *  then proves nothing either way. */
  beyond_lookup_window: boolean;
  /** Set when Paysafe couldn't be reached — the browser events are
   *  still worth reading, so this is reported rather than thrown. */
  lookup_error?: string | null;
  /** What the customer's browser reported while paying, oldest first. */
  browser_events: CheckoutEvent[];
  /** Newest first. Empty means no auth reached Paysafe in the window. */
  attempts: PaymentAttempt[];
};

/** One thing the browser saw during checkout. The half of the story the
 *  payment processor never sees: card entry and 3-D Secure happen in the
 *  browser, and anything that fails there produces no authorisation at
 *  all. */
export type CheckoutEvent = {
  id: number;
  /** 'opened' | 'BeforePayment' | 'DuringPayment' | 'sdk_error' |
   *  'declined' | 'payment_failed'. */
  stage: string;
  reason: string | null;
  user_agent: string | null;
  created_at: string;
};

/** Ask Paysafe what happened to this order's card payment.
 *
 *  Nothing about a failed payment is stored on our side — a decline
 *  leaves the order `pending`, which is all the "card incomplete" label
 *  means. Paysafe keeps the auth, and the order id is the
 *  `merchantRefNum` we sent, so this is the only way to see the reason.
 *  Live call to Paysafe, so it's on demand rather than part of the
 *  order load. */
export function getPaymentAttempts(
  idToken: string,
  id: string,
): Promise<PaymentAttempts> {
  return apiGet<PaymentAttempts>(
    `/api/v1/orders/${encodeURIComponent(id)}/payment-attempts`,
    idToken,
  );
}

/** Flip an order's status. Backend stamps the matching `*_at` field
 *  automatically and returns the updated order. */
export async function setOrderStatus(
  idToken: string,
  id: string,
  status: string,
): Promise<Order> {
  const res = await apiFetch(`/api/v1/orders/${encodeURIComponent(id)}`, idToken, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new ApiError(res.status, body);
  }
  return res.json();
}

export type OrdersAwaitingFulfillmentCount = { count: number };

/** Count of paid-but-not-yet-shipped orders, used by the admin shell
 *  banner. Cheap (single COUNT) so the banner polls it directly. */
export function countOrdersAwaitingFulfillment(
  idToken: string,
): Promise<OrdersAwaitingFulfillmentCount> {
  return apiGet<OrdersAwaitingFulfillmentCount>(
    '/api/v1/orders/awaiting-fulfillment/count',
    idToken,
  );
}

export type OrdersPendingCount = { count: number };

/** Count of pending (not-yet-paid) orders, used by the admin shell
 *  banner. Cheap (single COUNT) so the banner polls it directly. */
export function countOrdersPending(
  idToken: string,
): Promise<OrdersPendingCount> {
  return apiGet<OrdersPendingCount>('/api/v1/orders/pending/count', idToken);
}

// ---------------------------------------------------------------------------
// Sobeys price check (admin; cost is employee-visible, never public)
// ---------------------------------------------------------------------------

export type SobeysStatus =
  | 'under_cost'
  | 'on_sale'
  | 'ok'
  | 'unmatched'
  | 'rejected'
  | 'stale';

export type SobeysCandidate = {
  article_number: string;
  name: string;
  upc: string | null;
  price_cents: number | null;
  score: number;
};

export type SobeysReportRow = {
  tenant: string;
  item_id: string;
  name: string;
  upc: string | null;
  /** Our wholesale cost, integer cents. */
  cost_cents: number | null;
  /** Our shelf price, integer cents. */
  price_cents: number | null;
  status: SobeysStatus;
  /** What a walk-in customer pays at Sobeys today, cents; null if unknown. */
  effective_cents: number | null;
  match: {
    article_number: string | null;
    their_name: string | null;
    method: 'upc' | 'name' | 'manual' | null;
    confidence: number | null;
    confirmed: boolean;
    rejected: boolean;
    candidates: SobeysCandidate[];
  };
  sobeys: null | {
    regular_cents: number;
    /** Per unit; see sale_min_qty. */
    sale_cents: number | null;
    /** Units you must buy to get sale_cents each; 1 or null = plain sale. */
    sale_min_qty: number | null;
    sale_ends_at: string | null;
    sale_label: string | null;
    has_unparsed_promo: boolean;
    in_stock: boolean | null;
    observed_at: string;
    last_seen_at: string;
  };
};

export type SobeysRun = {
  id: number;
  started_at: string;
  finished_at: string | null;
  outcome: string | null;
  trigger: string;
  requests_made: number;
  items_matched: number;
  items_unmatched: number;
  last_error: string | null;
};

export type SobeysReport = {
  tenant: string;
  /** The retailer this report is for. */
  retailer: string;
  /** Every retailer with data, for the toggle. */
  retailers: string[];
  store_id: string | null;
  stale_after_days: number;
  last_run: SobeysRun | null;
  rows: SobeysReportRow[];
};

/** The price check for one retailer (`sobeys`, `superstore`). Omit for
 *  the server's default. */
export function getSobeysReport(
  idToken: string,
  retailer?: string,
): Promise<SobeysReport> {
  const q = retailer ? `?retailer=${encodeURIComponent(retailer)}` : '';
  return apiGet<SobeysReport>(`/api/v1/reports/sobeys-prices${q}`, idToken);
}

export type SobeysCount = { count: number };

/** Front-room items whose effective Sobeys price is below our cost. */
export function countSobeysUnderCost(idToken: string): Promise<SobeysCount> {
  return apiGet<SobeysCount>(
    '/api/v1/reports/sobeys-prices/undercut/count',
    idToken,
  );
}

/** Front-room items Sobeys currently has on sale. */
export function countSobeysOnSale(idToken: string): Promise<SobeysCount> {
  return apiGet<SobeysCount>(
    '/api/v1/reports/sobeys-prices/on-sale/count',
    idToken,
  );
}

async function expectNoContent(res: Response): Promise<void> {
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new ApiError(res.status, body);
  }
}

/** Link our item to a Sobeys article number by hand. Survives crawls. */
export async function linkSobeysMatch(
  idToken: string,
  tenant: string,
  itemId: string,
  articleNumber: string,
  retailer: string,
): Promise<void> {
  const res = await apiFetch(
    `/api/v1/sobeys/matches/${encodeURIComponent(tenant)}/${encodeURIComponent(itemId)}?retailer=${encodeURIComponent(retailer)}`,
    idToken,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ article_number: articleNumber }),
    },
  );
  await expectNoContent(res);
}

/** "Not sold at Sobeys": the crawler stops searching for this item. */
export async function rejectSobeysMatch(
  idToken: string,
  tenant: string,
  itemId: string,
  retailer: string,
): Promise<void> {
  const res = await apiFetch(
    `/api/v1/sobeys/matches/${encodeURIComponent(tenant)}/${encodeURIComponent(itemId)}/reject?retailer=${encodeURIComponent(retailer)}`,
    idToken,
    { method: 'POST' },
  );
  await expectNoContent(res);
}

/** Forget the link or rejection; the next crawl searches afresh. */
export async function clearSobeysMatch(
  idToken: string,
  tenant: string,
  itemId: string,
  retailer: string,
): Promise<void> {
  const res = await apiFetch(
    `/api/v1/sobeys/matches/${encodeURIComponent(tenant)}/${encodeURIComponent(itemId)}?retailer=${encodeURIComponent(retailer)}`,
    idToken,
    { method: 'DELETE' },
  );
  await expectNoContent(res);
}

// ---------------------------------------------------------------------------
// Customers (admin views)
// ---------------------------------------------------------------------------

export type CustomerAddress = {
  firstName?: string;
  lastName?: string;
  street?: string;
  additional?: string;
  city?: string;
  province?: string;
  postal?: string;
  country?: string;
  phone?: string;
} | null;

export type CustomerRow = {
  id: string;
  firebase_uid: string;
  email: string | null;
  email_verified: boolean;
  phone: string | null;
  display_name: string | null;
  created_at: string;
  updated_at: string;
  order_count: number;
  total_spent: string;
  last_order_at: string | null;
};

export type Customer = {
  id: string;
  firebase_uid: string;
  email: string | null;
  email_verified: boolean;
  phone: string | null;
  display_name: string | null;
  default_delivery_address: CustomerAddress;
  default_billing_address: CustomerAddress;
  created_at: string;
  updated_at: string;
};

export type CustomerOrderSummary = {
  id: string;
  status: string;
  total: string;
  created_at: string;
};

export type PointsBalanceSummary = {
  available: number;
  pending: number;
  lifetime_earned: number;
};

export type CustomerDetail = Customer & {
  recent_orders: CustomerOrderSummary[];
  friend_code: string | null;
  points: PointsBalanceSummary;
};

export type PointsLedgerEntry = {
  id: number;
  customer_id: string;
  order_id: string | null;
  source_customer: string | null;
  reason: 'purchase' | 'friend_code' | 'redemption' | 'refund_reversal' | 'adjustment';
  delta: number;
  redeemable_at: string | null;
  reversal_of: number | null;
  created_at: string;
};

export type CustomerPointsResponse = {
  available: number;
  pending: number;
  lifetime_earned: number;
  recent: PointsLedgerEntry[];
};

export type CustomersPage = {
  items: CustomerRow[];
  total: number;
  limit: number;
  offset: number;
};

export function listCustomers(
  idToken: string,
  search: string | null,
  limit: number,
  offset: number,
): Promise<CustomersPage> {
  const params = new URLSearchParams();
  if (search) params.set('search', search);
  params.set('limit', String(limit));
  params.set('offset', String(offset));
  return apiGet<CustomersPage>(`/api/v1/customers?${params.toString()}`, idToken);
}

export function getCustomer(idToken: string, id: string): Promise<CustomerDetail> {
  return apiGet<CustomerDetail>(`/api/v1/customers/${encodeURIComponent(id)}`, idToken);
}

export function getCustomerPoints(
  idToken: string,
  id: string,
): Promise<CustomerPointsResponse> {
  return apiGet<CustomerPointsResponse>(
    `/api/v1/customers/${encodeURIComponent(id)}/points`,
    idToken,
  );
}

// ---------------------------------------------------------------------------
// Transactions (cross-tenant Mongo collection; admin-only)
// ---------------------------------------------------------------------------

export type TransactionIdentifier = {
  vendor: string;
  location: string;
  id: string;
};

export type LineItemReconciliation = {
  user?: string | null;
  location?: string | null;
  dbName?: string | null;
  itemId?: string | null;
  name?: string | null;
};

export type TransactionLineItem = {
  id?: string | null;
  itemId?: string | null;
  name?: string | null;
  quantity?: number | null;
  price?: number | null;
  tax?: number | null;
  /** Legacy Square column, hardcoded `false` at ingest — it means
   *  nothing. Use `refunded_quantity` instead. */
  refunded?: boolean | null;
  /** Units of this line given back, summed over every refund against
   *  the sale. `0` when untouched. */
  refunded_quantity: number;
  /** `quantity - refunded_quantity`, floored at zero. Zero means the
   *  line is fully refunded. */
  refundable_quantity: number;
  reconciliation?: LineItemReconciliation | null;
};

export type Transaction = {
  identifier: TransactionIdentifier;
  line_items: TransactionLineItem[];
  subtotal?: number | null;
  total?: number | null;
  /** `'retail'` | `'employee'` — how the sale was priced. Absent on
   *  Square rows, which have no equivalent and are retail by
   *  definition. */
  price_basis?: string | null;
  /** Who rang it up. POS-native sales only. */
  cashier?: string | null;
  /** Money given back against this sale, integer cents, summed over
   *  every refund. Read from the stored refunds, so it matches what was
   *  actually handed back rather than a re-derivation. */
  refunded_subtotal: number;
  refunded_tax: number;
  refunded_total: number;
  created_at?: string | null;
  modified_at?: string | null;
  status?: string | null;
  state?: string | null;
};

/** Lists transactions for a single day (`YYYY-MM-DD`, UTC). */
export function listTransactions(
  idToken: string,
  day: string,
): Promise<Transaction[]> {
  const params = new URLSearchParams({ day });
  return apiGet<Transaction[]>(`/api/v1/transactions?${params.toString()}`, idToken);
}

export type ReconcileRequest = {
  name: string;
  itemId?: string;
  dbName?: string;
};

export type TransactionsSweepReport = {
  transactions_scanned: number;
  steps: number;
  reconciled: number;
  decremented: number;
  already_applied: number;
  cant_reconcile: number;
  applied: boolean;
  elapsed_ms: number;
};

export type SweepOutcome =
  | { kind: 'ok'; report: TransactionsSweepReport }
  | { kind: 'busy' }
  | { kind: 'not_configured' }
  | { kind: 'error'; status: number };

/**
 * Fire the auto-reconcile sweep on the backend. Returns a discriminated
 * union so the UI can distinguish "ran successfully", "another sweep is
 * already running" (409), "mongo not configured" (503), and other
 * errors — those signal three different states to the user.
 */
export async function sweepTransactions(idToken: string): Promise<SweepOutcome> {
  const res = await apiFetch('/api/v1/transactions/sweep', idToken, {
    method: 'POST',
  });
  if (res.ok) {
    const report = (await res.json()) as TransactionsSweepReport;
    return { kind: 'ok', report };
  }
  if (res.status === 409) return { kind: 'busy' };
  if (res.status === 503) return { kind: 'not_configured' };
  return { kind: 'error', status: res.status };
}

/** Wire shape of POST /api/v1/transactions/ingest. Mirrors
 *  `backroom_square_ingest::IngestReport`. */
export type TransactionsIngestReport = {
  fetched: number;
  inserted: number;
  already_present: number;
  skipped: number;
  pages: number;
  applied: boolean;
  elapsed_ms: number;
};

export type IngestOutcome =
  | { kind: 'ok'; report: TransactionsIngestReport }
  | { kind: 'busy' }
  | { kind: 'not_configured' }
  | { kind: 'square_error' }
  | { kind: 'error'; status: number };

/**
 * Fire the Square→Mongo transactions ingest on the backend. Server
 * uses the same 30-min lookback window the background tick does, so a
 * manual click sees the same coverage the next automatic tick would
 * have caught. Backfills outside that window go through the CLI.
 */
export async function ingestTransactions(idToken: string): Promise<IngestOutcome> {
  const res = await apiFetch('/api/v1/transactions/ingest', idToken, {
    method: 'POST',
  });
  if (res.ok) {
    const report = (await res.json()) as TransactionsIngestReport;
    return { kind: 'ok', report };
  }
  if (res.status === 409) return { kind: 'busy' };
  if (res.status === 503) return { kind: 'not_configured' };
  // Distinguish Square upstream failures from "everything else" so the
  // UI can say "couldn't reach Square" rather than the generic
  // "server error".
  if (res.status === 502) return { kind: 'square_error' };
  return { kind: 'error', status: res.status };
}

// ---------------------------------------------------------------------------
// Items → Square sync (admin-only; preview by default, apply on demand)
// ---------------------------------------------------------------------------

export type SquareSyncDbReport = {
  db_name: string;
  adds: number;
  readds: number;
  updates: number;
};

export type SquareSyncReport = {
  per_db: SquareSyncDbReport[];
  applied: boolean;
  /** True iff this run served from the server's in-memory cache
   *  populated by a prior run. False means the catalog was fetched
   *  fresh from Square. */
  used_cached_catalog: boolean;
  elapsed_ms: number;
};

export type SquareSyncOutcome =
  | { kind: 'ok'; report: SquareSyncReport }
  | { kind: 'busy' }
  | { kind: 'not_configured' }
  | { kind: 'error'; status: number };

/**
 * Run (or preview) the MongoDB → Square sync. `apply: false` is a
 * dry-run. Same discriminated-union shape as `sweepTransactions` —
 * the UI distinguishes ok / busy / not-configured / other-error.
 */
export async function syncSquare(
  idToken: string,
  apply: boolean,
): Promise<SquareSyncOutcome> {
  const res = await apiFetch('/api/v1/items/sync', idToken, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ apply }),
  });
  if (res.ok) {
    const report = (await res.json()) as SquareSyncReport;
    return { kind: 'ok', report };
  }
  if (res.status === 409) return { kind: 'busy' };
  if (res.status === 503) return { kind: 'not_configured' };
  return { kind: 'error', status: res.status };
}

export type SquareCacheClearOutcome =
  | { kind: 'ok'; cleared: boolean }
  | { kind: 'not_configured' }
  | { kind: 'error'; status: number };

/**
 * Drop the server's cached Square catalog. The next sync will refetch
 * from Square. Idempotent — calling on an empty cache returns
 * `cleared: false`.
 */
/** Backend's snapshot of the Square sync subsystem state. `configured`
 *  is false on servers without `[square]` credentials wired up — the
 *  cache-warm gate is off in that case. `cache_warm` is true iff
 *  someone has already run a Sync (or the server warmed it at boot via
 *  `[square].warm_cache_at_boot = true`). The items page uses this to
 *  grey out "+ Add item" until the cache is populated, since pushing a
 *  fresh item to Square needs the catalog cached for dedupe. */
export type SquareSyncStatus = {
  configured: boolean;
  cache_warm: boolean;
};

export function getSquareSyncStatus(idToken: string): Promise<SquareSyncStatus> {
  return apiGet<SquareSyncStatus>('/api/v1/items/sync/status', idToken);
}

export async function clearSquareCache(
  idToken: string,
): Promise<SquareCacheClearOutcome> {
  const res = await apiFetch('/api/v1/items/sync/clear-cache', idToken, {
    method: 'POST',
  });
  if (res.ok) {
    const body = (await res.json()) as { cleared: boolean };
    return { kind: 'ok', cleared: body.cleared };
  }
  if (res.status === 503) return { kind: 'not_configured' };
  return { kind: 'error', status: res.status };
}

// ---------------------------------------------------------------------------
// Reports (admin-only)
// ---------------------------------------------------------------------------

export type InventorySubtotal = {
  /** Sum of `cost_per_unit * count`, integer cents. */
  cost_cents: number;
  /** Sum of `price_per_unit * count`, integer cents. Zero when items
   *  contributing to this band have no price (only happens in the
   *  Absolute band — items with cost but missing price). */
  price_cents: number;
  /** Total units across items in this band. */
  unit_count: number;
};

export type TenantInventoryReport = {
  slug: string;
  label: string;
  /** All non-deleted items considered. */
  item_count: number;
  /** Items with no usable price (may still have cost). */
  missing_price_count: number;
  /** Items with price but no cost — these populate the Estimate band. */
  missing_cost_count: number;
  /** Items where cost was inferred from price (price/2 fallback). */
  estimate: InventorySubtotal;
  /** Items with an explicit cost. */
  absolute: InventorySubtotal;
  /** `estimate + absolute`. */
  total: InventorySubtotal;
};

export type InventoryReport = {
  tenants: TenantInventoryReport[];
};

export function getInventoryReport(idToken: string): Promise<InventoryReport> {
  return apiGet<InventoryReport>('/api/v1/reports/inventory', idToken);
}

export type GapCandidate = {
  item_id: string;
  item_name: string;
  /** RFC3339. */
  zeroed_at: string;
  /** RFC3339. */
  reset_at: string;
  gap_seconds: number;
};

export type CutoffAnalysis = {
  /** Human-readable date the bulk-zeroing ran on (e.g. "2026-01-14"). */
  zero_event_date: string;
  /** UTC search window used to find "zeroed" events. */
  window_start: string;
  window_end: string;
  items_scanned: number;
  items_with_zero: number;
  items_with_reset_after_zero: number;
  /** Item whose (first reset − zero) gap was the largest. Its `reset_at`
   *  is the proposed cutoff for the tenant. Null when no item in the
   *  window had a following reset. Equivalent to the P100 percentile. */
  longest_gap: GapCandidate | null;
  /** Nearest-rank P99 candidate; same shape as `longest_gap`. Use to see
   *  whether the proposed cutoff is being dragged out by a few late
   *  counts (P99 ≪ P100 → long tail). */
  p99: GapCandidate | null;
  /** Nearest-rank P95 candidate; same caveat as P99. */
  p95: GapCandidate | null;
  /** Items per UTC date of first-reset, sorted ascending. Use to
   *  eyeball a drop-off — the day after counting ended should show a
   *  sharp fall in items reset. */
  daily_histogram: DailyBucket[];
};

export type DailyBucket = {
  /** UTC calendar date, YYYY-MM-DD. */
  date: string;
  items: number;
};

export type TwoBucketCutoffs = {
  /** YYYY-MM-DD (Atlantic local EOD). */
  cutoff1: string;
  cutoff2: string;
};

export type TwoBucketStats = {
  items_via_bucket_1: number;
  items_via_bucket_2: number;
  items_uncounted: number;
};

export type YearEndTenantReport = {
  slug: string;
  label: string;
  /** Filled when this tenant's year-end strategy produces real totals. */
  report?: TenantInventoryReport | null;
  /** Filled when the tenant is still on HistoryWalkTbd — we surface the
   *  cutoff-discovery output for eyeball review. */
  cutoff_analysis?: CutoffAnalysis | null;
  /** Filled iff this tenant uses the two-bucket strategy. UI uses these
   *  to populate the date inputs. */
  active_cutoffs?: TwoBucketCutoffs | null;
  /** Filled iff this tenant uses the two-bucket strategy. */
  bucket_stats?: TwoBucketStats | null;
};

export type YearEndInventoryReport = {
  year: string;
  tenants: YearEndTenantReport[];
};

export type YearEndCutoffOverrides = {
  cutoff1?: string;
  cutoff2?: string;
};

export type IncomeYearRow = {
  year: number;
  /** Display label. Combined cross-location rows are "{year}"; per-shop
   *  subset rows are "{year} ({shop})". Use this for the row's first
   *  cell — labelling is a backend decision. */
  label: string;
  /** Sales BEFORE tax, integer cents. Combined rows: sum of in-store +
   *  fulfilled web orders. Per-shop subset rows: in-store only. */
  subtotal: number;
  /** Sales AFTER tax, integer cents. */
  total: number;
  /** In-store transactions contributing to the row. */
  transaction_count: number;
  /** Fulfilled (non-test) web orders contributing to the row. Always 0
   *  on per-shop subset rows. */
  order_count: number;
};

export type IncomeMonthRow = {
  year: number;
  /** 1..12. */
  month: number;
  subtotal: number;
  total: number;
  transaction_count: number;
  order_count: number;
};

export type IncomeWeekRow = {
  year: number;
  /** ISO week, 1..53. */
  week: number;
  /** Integer cents. */
  subtotal: number;
  total: number;
  transaction_count: number;
};

export type IncomeWeekTransactionCount = {
  year: number;
  /** ISO week, 1..53. */
  week: number;
  /** Bridgewater in-store transactions for the week (matches the
   *  legacy income page's default location filter). */
  in_store_count: number;
  /** Fulfilled (non-test) web orders attributed to the week. */
  order_count: number;
};

export type IncomeReport = {
  /** Inclusive first calendar year covered. */
  start_year: number;
  /** Inclusive last calendar year covered (current calendar year at request time). */
  end_year: number;
  /** One row per year in [start_year, end_year], ascending. */
  years: IncomeYearRow[];
  /** Sparse — only (year, month) combos that had transactions. UI fills the gaps. */
  months: IncomeMonthRow[];
  /** Sparse — only (year, ISO-week) combos that had transactions.
   *  Bridgewater only. UI pivots into per-year series. */
  weeks: IncomeWeekRow[];
  /** Sparse — only (year, ISO-week) combos with at least one
   *  in-store transaction OR fulfilled order. All locations + online. */
  weeks_transactions: IncomeWeekTransactionCount[];
};

export function getIncomeReport(idToken: string): Promise<IncomeReport> {
  return apiGet<IncomeReport>('/api/v1/reports/income', idToken);
}

/** One row in the "items without category" cleanup report. Shape lines
 *  up with what the embedded ItemEditDialog needs — `tenant` + `id` is
 *  enough to open the dialog; the other fields just decorate the row. */
export type ItemsWithoutCategoryRow = {
  id: string;
  tenant: string;
  /** Why this row is in the orphan list:
   *  - `missing`  — categoryId is absent / null / empty / whitespace.
   *  - `unknown`  — categoryId is set but doesn't match a real category
   *                 (usually because the category was deleted later). */
  reason: 'missing' | 'unknown';
  /** Present only when `reason === 'unknown'` — the stored value that
   *  no longer resolves. Useful as a breadcrumb when triaging. */
  bad_category_id?: string | null;
  /** Set iff this row is a variant (group.enabled + non-empty
   *  groupId + non-empty variantName). The id of the parent item.
   *  Rows reaching the report after auto-fix either aren't variants
   *  or have a parent that's missing / orphaned itself. */
  parent_id?: string | null;
  name: string;
  brand?: string | null;
  /** Integer cents. */
  price?: number | null;
  image_id?: string | null;
  /** Sum across every inventory location. */
  stock: number;
  updated_at?: string | null;
};

export type ItemsWithoutCategoryReport = {
  items: ItemsWithoutCategoryRow[];
  /** Count of variant orphans that the report auto-fixed on this call
   *  by inheriting the parent's categoryId. The fixed rows are NOT in
   *  `items` (they're no longer orphans). Idempotent: a subsequent
   *  call sees zero. */
  fixed_variants: number;
};

/** Temporary report — surfaces every non-deleted item missing a
 *  category, so the operator can assign one via the edit dialog. */
export function getItemsWithoutCategory(
  idToken: string,
): Promise<ItemsWithoutCategoryReport> {
  return apiGet<ItemsWithoutCategoryReport>(
    '/api/v1/reports/items-without-category',
    idToken,
  );
}

export function getInventoryYearEndReport(
  idToken: string,
  year: string,
  overrides?: YearEndCutoffOverrides,
): Promise<YearEndInventoryReport> {
  const params = new URLSearchParams();
  if (overrides?.cutoff1) params.set('cutoff1', overrides.cutoff1);
  if (overrides?.cutoff2) params.set('cutoff2', overrides.cutoff2);
  const qs = params.toString();
  const suffix = qs ? `?${qs}` : '';
  return apiGet<YearEndInventoryReport>(
    `/api/v1/reports/inventory/year-end/${encodeURIComponent(year)}${suffix}`,
    idToken,
  );
}

// ---------------------------------------------------------------------------
// Sales by category report
// ---------------------------------------------------------------------------

/** Rolling-window keys the sales-by-category report accepts. Maps 1:1
 *  to the backend's `resolve_period` (day=1 … year=365 days). */
export type CategorySalesPeriod =
  | 'day'
  | 'week'
  | 'month'
  | '3months'
  | '6months'
  | 'year';

/** One direct-category bucket. `category_id` is absent for the
 *  "uncategorized" bucket (sold items with no category set). */
export type CategorySalesEntry = {
  category_id?: string;
  /** Units sold in the window (may be fractional). */
  units: number;
  /** Gross revenue, integer cents (pre-tax, pre-discount). */
  revenue_cents: number;
};

/** A node in a tenant's category tree. `parent_id === "1"` (or absent)
 *  marks a root category — matches the stored root marker. */
export type CategoryNode = {
  id: string;
  name: string;
  parent_id?: string;
};

export type TenantCategorySales = {
  slug: string;
  label: string;
  /** Per-direct-category sales; unsorted. The UI rolls up + sorts. */
  categories: CategorySalesEntry[];
  /** Full category tree so the UI can roll leaves up to their root. */
  tree: CategoryNode[];
};

export type CategorySalesReport = {
  /** Resolved period key echoed back by the server. */
  period: CategorySalesPeriod;
  /** Window length in days the period maps to. */
  window_days: number;
  tenants: TenantCategorySales[];
  /** Cross-tenant in-store sales absent from every category chart, split
   *  by why. `pending_*` awaits reconciliation (transient); `custom_*`
   *  is typed register lines with no catalog object (permanent). Global
   *  — not attributable to a tenant. */
  uncatalogued: {
    pending_units: number;
    pending_revenue_cents: number;
    custom_units: number;
    custom_revenue_cents: number;
  };
};

/** Sold units + gross revenue bucketed by category over a rolling
 *  window, per Square-synced tenant, plus the category tree for
 *  client-side root/leaf roll-up. */
export function getCategorySalesReport(
  idToken: string,
  period: CategorySalesPeriod,
): Promise<CategorySalesReport> {
  return apiGet<CategorySalesReport>(
    `/api/v1/reports/category-sales?period=${encodeURIComponent(period)}`,
    idToken,
  );
}

/** One day on the daily-sales-&-weather timeline. Sales are always
 *  present (zero-filled on days with none); weather fields are absent
 *  when the day isn't cached yet — the Open-Meteo archive lags ~5 days,
 *  so the most recent days render with no weather until it catches up. */
export type DailySalesRow = {
  /** Local calendar day, `YYYY-MM-DD`. */
  day: string;
  /** In-store Bridgewater sales before tax, dollars. */
  subtotal: number;
  /** In-store Bridgewater sales after tax, dollars. */
  total: number;
  transaction_count: number;
  /** Daily high (°C). */
  temp_max_c?: number;
  /** Daily low (°C). */
  temp_min_c?: number;
  /** Precipitation (mm). */
  precip_mm?: number;
  /** Snowfall (cm). */
  snowfall_cm?: number;
  /** Daily mean cloud cover, percent (0–100). The report derives the
   *  sunny/cloudy icon from this + precip (Open-Meteo's raw weather_code
   *  reflects the day's worst moment, so it over-reports cloud/rain). */
  cloud_cover_mean?: number;
  /** Hours of measurable precipitation in the day. */
  precipitation_hours?: number;
};

export type DailySalesReport = {
  /** Resolved period key echoed by the server. */
  period: CategorySalesPeriod;
  /** Window length in days the period maps to. */
  window_days: number;
  /** Inclusive window bounds, `YYYY-MM-DD`. */
  start_date: string;
  end_date: string;
  /** One row per day in `[start_date, end_date]`, ascending + continuous. */
  days: DailySalesRow[];
};

/** Bridgewater in-store daily sales paired with that day's weather, over
 *  a rolling window. Reuses the category report's period keys. */
export function getDailySalesReport(
  idToken: string,
  period: CategorySalesPeriod,
): Promise<DailySalesReport> {
  return apiGet<DailySalesReport>(
    `/api/v1/reports/daily-sales?period=${encodeURIComponent(period)}`,
    idToken,
  );
}

// ---------------------------------------------------------------------------
// Annual SKU-level sales report (god-only)
// ---------------------------------------------------------------------------

/** One catalogued item's total for a year at one shop. */
export type SkuSalesRow = {
  /** Computed display SKU (`{prefix}{id[0..6].upper()}`); '' if the
   *  tenant is unrecognised. Never stored — the server computes it so
   *  the CSV and the table can't disagree. */
  sku: string;
  item_id: string;
  /** Tenant slug, e.g. `backroom`. */
  tenant: string;
  /** Human label for the tenant, e.g. "The Back Room". */
  tenant_label: string;
  name: string;
  category?: string;
  units: number;
  /** Gross, integer cents. Pre-tax, pre-discount. */
  gross_cents: number;
};

/** A named catch-all within a shop section. Always present, even at 0. */
export type SkuSalesBucket = {
  units: number;
  gross_cents: number;
};

/** One shop. Square registers roll up to shops server-side, so both
 *  Bridgewater tills arrive as a single section. */
export type SkuSalesSection = {
  label: string;
  /** Raw Square location codes behind this section — shown for
   *  unmapped terminals so they stay traceable. */
  codes: string[];
  rows: SkuSalesRow[];
  /** Vapes: lines hand-reconciled to the "." placeholder, plus
   *  uncatalogued lines at the standard $40/$50 price points. */
  vapes: SkuSalesBucket;
  /** Sales with no usable catalog link (never reconciled, or typed
   *  custom lines). */
  general: SkuSalesBucket;
  units: number;
  gross_cents: number;
};

/** Data-health panel for the selected year. Surfaces a bad year before
 *  the CSV gets sent to anyone outside. */
export type SkuSalesSelfCheck = {
  /** Share of gross dollars the report can name — catalogued items plus
   *  vapes. Vapes count as identified: the "." marker and the $40/$50
   *  price points classify them well enough for this report, so leaving
   *  them in the remainder would understate the year's coverage. */
  identified_pct: number;
  /** Vapes alone — a subset of `identified_pct`. */
  vapes_pct: number;
  /** The genuine remainder: no catalog link and no vape signal. */
  general_pct: number;
  /** Summed line items, pre-discount — same basis as the report's
   *  gross figures. */
  line_gross_cents: number;
  /** Per-line discounts applied during the year. The header subtotal is
   *  what customers actually paid, so it's already net of these. */
  discounts_cents: number;
  header_subtotal_cents: number;
  /** `(line_gross - discounts) - header_subtotal`, signed. */
  diff_cents: number;
  /** `diff_cents` as a percentage of the year's takings — what the
   *  pass/fail decision is actually made on. Small drift (well under
   *  1%) is expected legacy-POS noise, not a defect. */
  diff_pct: number;
  tie_out_ok: boolean;
  transaction_count: number;
};

export type SkuSalesReport = {
  year: number;
  /** Years with transactions, descending. Drives the picker. */
  available_years: number[];
  /** Only shops with sales in `year`, gross-descending. */
  sections: SkuSalesSection[];
  units: number;
  gross_cents: number;
  self_check: SkuSalesSelfCheck;
};

/** Per-SKU gross sales for a calendar year, sectioned by shop.
 *  God-only — a non-god caller gets 403. */
export function getSkuSalesReport(
  idToken: string,
  year?: number,
): Promise<SkuSalesReport> {
  const q = year === undefined ? '' : `?year=${encodeURIComponent(year)}`;
  return apiGet<SkuSalesReport>(`/api/v1/reports/sku-sales${q}`, idToken);
}

/** Fetch the CSV rendering of the same report and hand it to the
 *  browser as a download. Goes through `apiFetch` rather than a plain
 *  `<a href>` because the endpoint needs the Firebase bearer token —
 *  a link element can't carry one. */
export async function downloadSkuSalesCsv(
  idToken: string,
  year: number,
): Promise<void> {
  const res = await apiFetch(
    `/api/v1/reports/sku-sales?year=${encodeURIComponent(year)}&format=csv`,
    idToken,
  );
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new ApiError(res.status, body);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `sales-by-sku-${year}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Release the object URL once the click has been dispatched; holding
  // it pins the blob in memory for the life of the document.
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Items + categories (per-tenant Mongo; admin-only)
// ---------------------------------------------------------------------------

/** A top-level bucket on the items page. `db_name` is not exposed — the
 *  frontend only knows the slug; backroom-server maps it to a Mongo db. */
export type ItemTenant = {
  slug: string;
  label: string;
  /** URL-encodeable path prefix used in Sirv image URLs. Empty when
   *  `enabled` is false. */
  image_root: string;
  /** Single-letter SKU prefix. Display SKU is `{sku_prefix}{id[0..6].upper()}`. */
  sku_prefix: string;
  enabled: boolean;
  /** True when this tenant supports admin-driven category creation
   *  (Postgres-only tenants today; gate widens once items + transactions
   *  are also Postgres-canonical). The frontend uses this to gate the
   *  "+ New Category" button. */
  can_create_categories: boolean;
  /** True when items in this tenant push to Square. Used to gate the
   *  optimistic "Square cache must be warm" disable on the
   *  `+ Add item` button — a non-synced tenant (the florist archive
   *  today) bypasses that check entirely. */
  is_synced_to_square: boolean;
};

export type AdminCategory = {
  id: string;
  name: string;
  parent_id?: string | null;
  image_id?: string | null;
  hidden?: boolean | null;
  order?: number | null;
};

export type ItemCodes = {
  upc?: string | null;
  sku?: string | null;
};

export type ItemFlags = {
  discontinued?: boolean | null;
  hidden?: boolean | null;
  special?: boolean | null;
  deleted?: boolean | null;
  /** Sells at 0% HST, online and on the register, and syncs to Square
   *  with no tax attached. */
  taxExempt?: boolean | null;
};

export type ItemGroup = {
  enabled?: boolean | null;
  group_id?: string | null;
  variant_name?: string | null;
};

export type InventoryView = {
  count: number;
  updated_at?: string | null;
  reset_at?: string | null;
};

export type AdminItem = {
  id: string;
  name: string;
  description?: string | null;
  category_id?: string | null;
  brand?: string | null;
  /** Integer cents. */
  price?: number | null;
  cost?: number | null;
  /** Shipping weight, integer grams. Frontend converts to/from the
   *  operator's chosen unit (kg / g / lb) for display + entry. */
  weight_g?: number | null;
  /** Bounding-box dimensions, integer centimetres. Inner fields are
   *  individually optional so partial entries survive the round-trip. */
  dimensions_cm?: {
    width?: number | null;
    length?: number | null;
    height?: number | null;
  } | null;
  image_id?: string | null;
  codes?: ItemCodes | null;
  flags?: ItemFlags | null;
  group?: ItemGroup | null;
  /** Mongo stores supplierUrls as an array; only [0] is exposed in the edit UI. */
  supplier_urls?: string[] | null;
  /** Stored as `distributorUrl` in Mongo; shown as "Manufacturer URL" in the UI. */
  distributor_url?: string | null;
  /** Sum of inventory across every location. */
  stock: number;
  /** Per-location current count + freshness. History is stripped server-side. */
  inventory: Record<string, InventoryView>;
  /** Per-personality descriptions (personality slug -> text). The
   *  top-level `description` field is the primary; this map is the
   *  per-voice catalog. Always present (possibly empty). */
  descriptions: Record<string, string>;
  updated_at?: string | null;
  /** When the "Featured" toggle was most recently flipped on. `null` /
   *  absent means the item is not currently featured. Drive the edit
   *  dialog's toggle from `featured_at != null`. */
  featured_at?: string | null;
  /** When the item was soft-deleted. `null` / absent means the item
   *  is visible (and shown on the customer-facing storefront). The
   *  admin grid uses this to render tombstones (light-red bg + faded)
   *  when the "Show deleted" toggle is on. */
  deleted_at?: string | null;
};

export function listItemTenants(idToken: string): Promise<ItemTenant[]> {
  return apiGet<ItemTenant[]>('/api/v1/items/tenants', idToken);
}

/** A single hit from the cross-tenant item search. The picker shows
 *  `{brand} - {name} - $price` and round-trips `id` + `tenant` + `db_name`
 *  back to the reconcile endpoint on selection. */
export type ItemSearchHit = {
  id: string;
  /** Public tenant slug — same value as `ItemTenant.slug`. */
  tenant: string;
  /** Mongo database name; recorded on the reconciliation document. */
  db_name: string;
  brand?: string | null;
  name: string;
  /** Integer cents. Matches the rest of the items API. */
  price?: number | null;
  /** Sirv image id. Use the matching tenant's `image_root` to build
   *  the URL. Optional — older items may lack one. */
  image_id?: string | null;
  /** Sum of inventory across every location, integer units. Computed
   *  server-side from `inventory.*.count`. Always present; defaults
   *  to 0 when the item has no inventory entries at all. */
  stock: number;
  /** `flags.hidden` from Mongo, surfaced so the search results page
   *  can dim hidden items the same way the category page does. */
  hidden: boolean;
};

/** Cross-tenant catalog search. `priceCents`, when provided, restricts
 *  to items whose `price` matches exactly — useful when reconciling a
 *  known-priced line item against the catalog. */
export function searchItems(
  idToken: string,
  q: string,
  opts?: { priceCents?: number; limit?: number },
): Promise<ItemSearchHit[]> {
  const params = new URLSearchParams({ q });
  if (opts?.priceCents != null) params.set('price', String(opts.priceCents));
  if (opts?.limit != null) params.set('limit', String(opts.limit));
  return apiGet<ItemSearchHit[]>(
    `/api/v1/items/search?${params.toString()}`,
    idToken,
  );
}

/** Discriminator for which step of the lookup cascade matched. `none`
 *  means nothing matched at any step (frontend renders "no results").
 *  The four values mirror `LookupResponse.kind` server-side; update
 *  both ends if you add a new step. */
export type ItemLookupKind = 'sku' | 'upc' | 'name' | 'none';

export type ItemLookupResponse = {
  kind: ItemLookupKind;
  hits: ItemSearchHit[];
};

/** Smart catalog lookup for the items-page search bar. Server-side
 *  cascade: SKU exact (7 chars, tenant prefix) → UPC exact (8-14
 *  digits) → free-text name. First step that returns hits wins.
 *  Use `kind` to distinguish UX states (e.g. show a "code match" chip
 *  on SKU / UPC hits). */
export function lookupItems(
  idToken: string,
  q: string,
  limit?: number,
): Promise<ItemLookupResponse> {
  const params = new URLSearchParams({ q });
  if (limit != null) params.set('limit', String(limit));
  return apiGet<ItemLookupResponse>(
    `/api/v1/items/lookup?${params.toString()}`,
    idToken,
  );
}

// ---------------------------------------------------------------------------
// Gemini personalities (shared registry, read from atlanticgrowshop)
// ---------------------------------------------------------------------------

export type Personality = {
  id: string;
  displayName: string;
  disclaimer?: string;
};

export function listPersonalities(idToken: string): Promise<Personality[]> {
  return apiGet<Personality[]>('/api/v1/personalities', idToken);
}

// ---------------------------------------------------------------------------
// Scrape: generic supplier-page metadata extraction
// ---------------------------------------------------------------------------

/** Result of POST /api/v1/scrape/description. `description` is null
 *  when the fetch succeeded but no description-shaped metadata was on
 *  the page — distinct from a fetch failure (which throws ApiError). */
export type ScrapeDescriptionResult = {
  description: string | null;
};

export async function scrapeDescription(
  idToken: string,
  url: string,
): Promise<ScrapeDescriptionResult> {
  const res = await apiFetch('/api/v1/scrape/description', idToken, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ url }),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new ApiError(res.status, body);
  }
  return res.json();
}

// ---------------------------------------------------------------------------
// AI generation
// ---------------------------------------------------------------------------

/** Result of POST /api/v1/items/.../generate-description/:personality.
 *  `text` is empty string (not null) when Gemini returned UNKNOWN —
 *  the frontend checks `text.length === 0` to surface "couldn't
 *  generate" without distinguishing it from a fetch failure. */
export type GenerateDescriptionResult = {
  text: string;
};

export async function generateDescription(
  idToken: string,
  tenant: string,
  id: string,
  personalitySlug: string,
): Promise<GenerateDescriptionResult> {
  const res = await apiFetch(
    `/api/v1/items/${encodeURIComponent(tenant)}/${encodeURIComponent(id)}/generate-description/${encodeURIComponent(personalitySlug)}`,
    idToken,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    },
  );
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new ApiError(res.status, body);
  }
  return res.json();
}

export function listCategories(
  idToken: string,
  tenant: string,
): Promise<AdminCategory[]> {
  return apiGet<AdminCategory[]>(
    `/api/v1/items/${encodeURIComponent(tenant)}/categories`,
    idToken,
  );
}

/** Request body for POST /api/v1/items/:tenant/categories.
 *  Camel-cased to match the backend's `serde(rename_all = "camelCase")`
 *  on `CategoryCreate`. `parentId` defaults to the root marker `"1"`
 *  server-side when null/missing — pass the current category's id when
 *  nesting. */
export type CreateCategoryBody = {
  name: string;
  parentId?: string | null;
  imageId?: string | null;
  hidden?: boolean | null;
  order?: number | null;
};

/** Create a category in the named tenant. Backend gates this to
 *  Postgres-only tenants today (`can_create_categories` on
 *  `ItemTenant`); a 405 / 404 from the server means the gate is
 *  closed for that tenant. */
export async function createCategory(
  idToken: string,
  tenant: string,
  body: CreateCategoryBody,
): Promise<AdminCategory> {
  const res = await apiFetch(
    `/api/v1/items/${encodeURIComponent(tenant)}/categories`,
    idToken,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new ApiError(res.status, text);
  }
  return (await res.json()) as AdminCategory;
}

export function listItemsByCategory(
  idToken: string,
  tenant: string,
  categoryId: string,
  options?: { includeDeleted?: boolean },
): Promise<AdminItem[]> {
  const params = new URLSearchParams();
  if (options?.includeDeleted) params.set('include_deleted', 'true');
  const qs = params.toString();
  return apiGet<AdminItem[]>(
    `/api/v1/items/${encodeURIComponent(tenant)}/category/${encodeURIComponent(categoryId)}${qs ? `?${qs}` : ''}`,
    idToken,
  );
}

export function getItem(
  idToken: string,
  tenant: string,
  id: string,
): Promise<AdminItem> {
  return apiGet<AdminItem>(
    `/api/v1/items/${encodeURIComponent(tenant)}/${encodeURIComponent(id)}`,
    idToken,
  );
}

/** Partial-update shape sent to PATCH /api/v1/items/:tenant/:id.
 *  Mirrors `backroom_mongodb::ItemUpdate`. Only fields the admin actually
 *  changed should be included; absent fields are left untouched. The
 *  backend uses `deny_unknown_fields`, so don't pass anything beyond
 *  what's listed here without updating the Rust struct too. */
export type ItemPatch = {
  name?: string;
  description?: string;
  brand?: string;
  categoryId?: string;
  /** Price in integer cents. */
  price?: number;
  cost?: number;
  /** Shipping weight, integer grams. */
  weightG?: number;
  /** Bounding-box dimensions, integer centimetres. `$set`s the whole
   *  nested object; partial axes are allowed inside. */
  dimensionsCm?: { width?: number; length?: number; height?: number };
  imageId?: string;
  upc?: string;
  // sku is intentionally NOT in this type. tqos-server clears
  // codes.sku on read and computes the display SKU as
  // `{tenant_prefix}{id[0..6].toUpperCase()}`. The backend struct
  // omits the field too — sending it would be a 400.
  hidden?: boolean;
  discontinued?: boolean;
  special?: boolean;
  /** 0% HST on every line that sells this item, and no tax id on the
   *  Square catalog object. Shown in the UI as "Zero Rated" — the
   *  accounting term for what this actually does; the wire name stays
   *  `taxExempt`. */
  taxExempt?: boolean;
  /** "Featured product" toggle. Unlike the other booleans this one
   *  carries a timestamp server-side: `true` stamps `featured_at = now()`,
   *  `false` clears it to NULL. The storefront card shows the
   *  most-recently-stamped items. */
  featured?: boolean;
  /** Soft-delete toggle. `true` stamps `deleted_at = COALESCE(deleted_at,
   *  now())` (sticky — re-clicking doesn't reset the timestamp);
   *  `false` clears it (restore). Filtered out of the customer-facing
   *  storefront unconditionally. */
  deleted?: boolean;
  /** Replaces `supplierUrls[0]` only; the rest of the array is preserved. */
  supplierUrl?: string;
  /** Mongo field is `distributorUrl`; UI labels it "Manufacturer URL". */
  distributorUrl?: string;
  /** Map of inventory-location key (e.g. "liverpool") -> new count. */
  inventory?: Record<string, number>;
  /** Map of personality slug -> new text. Empty string clears the
   *  entry. The primary copy rides the top-level `description` field. */
  descriptions?: Record<string, string>;
};

/** Shape sent to POST /api/v1/items/:tenant. `name` and `categoryId`
 *  are required; everything else mirrors `ItemPatch`. Variant/group
 *  fields are intentionally absent — the create dialog only makes leaf
 *  items, same as the edit dialog. */
export type ItemCreate = {
  name: string;
  categoryId: string;
  brand?: string;
  description?: string;
  /** Integer cents. */
  price?: number;
  cost?: number;
  /** Shipping weight, integer grams. */
  weightG?: number;
  /** Bounding-box dimensions, integer centimetres. */
  dimensionsCm?: { width?: number; length?: number; height?: number };
  upc?: string;
  supplierUrl?: string;
  distributorUrl?: string;
  hidden?: boolean;
  discontinued?: boolean;
  special?: boolean;
  taxExempt?: boolean;
  /** Initial per-location counts. Empty/omitted = start with no
   *  inventory entries. */
  inventory?: Record<string, number>;
};

export async function createItem(
  idToken: string,
  tenant: string,
  body: ItemCreate,
): Promise<AdminItem> {
  const res = await apiFetch(
    `/api/v1/items/${encodeURIComponent(tenant)}`,
    idToken,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    throw new ApiError(res.status, txt);
  }
  return res.json();
}

/** Tri-state flag update — `null` / omitted = leave the flag alone;
 *  `true` / `false` = set explicitly. Independent per flag. */
export type BulkFlagSet = {
  special?: boolean | null;
  hidden?: boolean | null;
  discontinued?: boolean | null;
  taxExempt?: boolean | null;
};

export type BulkFlagOutcome = {
  /** Items that the filter matched. Includes ones already in the
   *  target state — they fall inside the blast radius even though
   *  they don't get a write. */
  matched: number;
  /** Items the update actually changed. Always 0 when applied=false. */
  modified: number;
  /** Mirrors the apply flag sent in the request. */
  applied: boolean;
};

/** Bulk-flag toggle scoped to a category (optionally recursive).
 *  Server walks descendants when `recursive` is true. Use
 *  `apply=false` to fetch the matched count for the preview, then
 *  re-fire with `apply=true` to actually write. */
export async function bulkFlagItems(
  idToken: string,
  tenant: string,
  body: {
    categoryId: string;
    recursive: boolean;
    set: BulkFlagSet;
    apply: boolean;
  },
): Promise<BulkFlagOutcome> {
  const res = await apiFetch(
    `/api/v1/items/${encodeURIComponent(tenant)}/bulk-flag`,
    idToken,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        category_id: body.categoryId,
        recursive: body.recursive,
        set: body.set,
        apply: body.apply,
      }),
    },
  );
  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    throw new ApiError(res.status, txt);
  }
  return res.json();
}

export async function patchItem(
  idToken: string,
  tenant: string,
  id: string,
  patch: ItemPatch,
): Promise<AdminItem> {
  const res = await apiFetch(
    `/api/v1/items/${encodeURIComponent(tenant)}/${encodeURIComponent(id)}`,
    idToken,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    },
  );
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new ApiError(res.status, body);
  }
  return res.json();
}

/** Upload a new product image. The server fetches the URL, converts to
 *  JPG, uploads to Sirv, and patches Mongo `imageId`. Returns the
 *  updated item (with the new `image_id` populated). */
export async function setItemImage(
  idToken: string,
  tenant: string,
  id: string,
  imageUrl: string,
): Promise<AdminItem> {
  const res = await apiFetch(
    `/api/v1/items/${encodeURIComponent(tenant)}/${encodeURIComponent(id)}/image`,
    idToken,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ image_url: imageUrl }),
    },
  );
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new ApiError(res.status, body);
  }
  return res.json();
}

/** Direct multipart upload of a product image. The fallback for supplier
 *  pages the server can't fetch itself (Cloudflare bot challenges,
 *  login walls): the admin saves the image in their browser — which
 *  passes those challenges — and uploads the file. Server converts to
 *  JPG, uploads to Sirv, patches Mongo `imageId`, and returns the
 *  updated item (same shape as `setItemImage`). Also backs the edit
 *  dialog's paste-from-clipboard action, so `file` is typed as `Blob`
 *  (a pasted image has no filename). */
export async function uploadItemImage(
  idToken: string,
  tenant: string,
  id: string,
  file: Blob,
): Promise<AdminItem> {
  const form = new FormData();
  form.append('file', file);
  // Don't set Content-Type — the browser fills in the multipart
  // boundary. apiFetch only sets Accept + Authorization.
  const res = await apiFetch(
    `/api/v1/items/${encodeURIComponent(tenant)}/${encodeURIComponent(id)}/image/upload`,
    idToken,
    { method: 'POST', body: form },
  );
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new ApiError(res.status, body);
  }
  return res.json();
}

export type RepairLineDetail = {
  txn_id: string;
  /** `ok`, `skip`, or `error` — see report description in the handler. */
  outcome: 'ok' | 'skip' | 'error';
  /** Damaged lines fixed in this transaction. Zero on skip/error. */
  repaired: number;
  /** Human-readable reason on skip/error. */
  message?: string;
};

export type RepairReport = {
  scanned: number;
  repaired: number;
  skipped: number;
  failed: number;
  elapsed_ms: number;
  /** Capped server-side (~200 entries) to keep the response bounded. */
  details: RepairLineDetail[];
};

export type RepairOutcome =
  | { kind: 'ok'; report: RepairReport }
  | { kind: 'not_configured' }
  | { kind: 'bad_date' }
  | { kind: 'error'; status: number };

/**
 * One-shot fix for transactions damaged by the legacy sweep bug
 * (line items overwritten by their own reconciliation document). Server
 * walks `created_at >= from` for any transaction whose line_items contain
 * a top-level `user` field, re-fetches the order from Square, and
 * rebuilds the damaged lines while preserving the reconciliation data.
 */
export async function repairTransactions(
  idToken: string,
  from: string,
): Promise<RepairOutcome> {
  const params = new URLSearchParams({ from });
  const res = await apiFetch(
    `/api/v1/transactions/repair?${params.toString()}`,
    idToken,
    { method: 'POST' },
  );
  if (res.ok) {
    const report = (await res.json()) as RepairReport;
    return { kind: 'ok', report };
  }
  if (res.status === 503) return { kind: 'not_configured' };
  if (res.status === 400) return { kind: 'bad_date' };
  return { kind: 'error', status: res.status };
}

export async function reconcileLineItem(
  idToken: string,
  vendor: string,
  location: string,
  txnId: string,
  lineItemId: string,
  body: ReconcileRequest,
): Promise<void> {
  const path =
    `/api/v1/transactions/${encodeURIComponent(vendor)}` +
    `/${encodeURIComponent(location)}` +
    `/${encodeURIComponent(txnId)}` +
    `/line-items/${encodeURIComponent(lineItemId)}`;
  const res = await apiFetch(path, idToken, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: body.name,
      item_id: body.itemId ?? '',
      db_name: body.dbName ?? null,
    }),
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    throw new ApiError(res.status, txt);
  }
}

// ---------------------------------------------------------------------------
// News
// ---------------------------------------------------------------------------

export type NewsPost = {
  id: string;
  date: string;
  body: string;
  images: string[];
  facebook?: string;
  // Populated only when the response was served to an admin caller.
  // Public/anonymous responses never include soft-deleted rows in the
  // first place, so the field is absent for guests and unset for
  // live posts.
  deletedAt?: string;
};

export type NewsListResponse = {
  items: NewsPost[];
  total: number;
  limit: number;
  offset: number;
};

// Same endpoint for guest and admin reads — the server widens the
// visible set to include soft-deleted rows when the bearer token
// resolves to a principal with the `admin` role. Pass `idToken` from
// `useAuth()`; pass `null` for the public/anonymous path.
export async function listNews(
  idToken: string | null,
  limit = 50,
  offset = 0,
): Promise<NewsListResponse> {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  params.set('offset', String(offset));
  const res = await apiFetch(`/api/v1/news?${params.toString()}`, idToken);
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new ApiError(res.status, body);
  }
  return res.json();
}

export async function getNews(
  idToken: string | null,
  id: string,
): Promise<NewsPost> {
  const res = await apiFetch(`/api/v1/news/${encodeURIComponent(id)}`, idToken);
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new ApiError(res.status, body);
  }
  return res.json();
}

export type NewsCreate = {
  id: string;
  date: string;
  body: string;
  images?: string[];
  facebook?: string | null;
};

export async function createNews(
  idToken: string,
  input: NewsCreate,
): Promise<NewsPost> {
  const res = await apiFetch('/api/v1/news', idToken, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new ApiError(res.status, body);
  }
  return res.json();
}

export type NewsPatch = {
  date?: string;
  body?: string;
  images?: string[];
  // Use `null` to clear the existing Facebook link; omit the key to
  // leave it untouched. Matches the backend's Option<Option<T>> shape.
  facebook?: string | null;
};

export async function patchNews(
  idToken: string,
  id: string,
  patch: NewsPatch,
): Promise<NewsPost> {
  const res = await apiFetch(`/api/v1/news/${encodeURIComponent(id)}`, idToken, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new ApiError(res.status, body);
  }
  return res.json();
}

// Soft delete. Returns the updated post (now with `deletedAt` stamped)
// so the caller can flip its render to tombstone state without a
// follow-up GET.
export async function deleteNews(
  idToken: string,
  id: string,
): Promise<NewsPost> {
  const res = await apiFetch(`/api/v1/news/${encodeURIComponent(id)}`, idToken, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new ApiError(res.status, body);
  }
  return res.json();
}

// Undelete a soft-deleted post. Idempotent on a live post — the
// response shape is the same so the UI can use one optimistic update
// path for either action.
export async function restoreNews(
  idToken: string,
  id: string,
): Promise<NewsPost> {
  const res = await apiFetch(
    `/api/v1/news/${encodeURIComponent(id)}/restore`,
    idToken,
    { method: 'POST' },
  );
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new ApiError(res.status, body);
  }
  return res.json();
}

export type UploadImageResponse = { url: string };

/// Multipart upload of a single image file. Server re-encodes as JPG,
/// uploads to Sirv under `News/`, and returns the fully-qualified URL.
/// The caller drops the URL straight into the post's `images` array.
export async function uploadNewsImage(
  idToken: string,
  file: File,
): Promise<UploadImageResponse> {
  const form = new FormData();
  form.append('file', file);
  // Don't set Content-Type explicitly — the browser fills in the
  // multipart boundary. apiFetch only sets Accept + Authorization.
  const res = await apiFetch('/api/v1/news/images', idToken, {
    method: 'POST',
    body: form,
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new ApiError(res.status, body);
  }
  return res.json();
}

/// Render-time helper. New posts store fully-qualified Sirv URLs in
/// `images`; legacy seed entries (4 posts as of writing) stored bare
/// filenames served from `webapp/public/images/feed/`. Return the
/// correct `src` for either shape so callers don't have to branch.
export function newsImageUrl(image: string): string {
  if (/^https?:\/\//i.test(image)) return image;
  return `/images/feed/${image}`;
}
